import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { demoProfiles, demoRecipes } from "./data/demo";
import { createClientId } from "./lib/id";
import { supabase } from "./lib/supabase";
import type { Recipe, RecipeDraft } from "./types";
import { useAuth } from "./AuthContext";

interface RecipeContextValue {
  recipes: Recipe[];
  loading: boolean;
  error: string | null;
  saveRecipe: (draft: RecipeDraft, recipeId?: string, photoFile?: File | null) => Promise<Recipe>;
  updateRecipeShares: (recipeId: string, groupIds: string[], userIds: string[]) => Promise<Recipe>;
  deleteRecipe: (recipeId: string) => Promise<void>;
  toggleFavorite: (recipeId: string) => Promise<void>;
}

const RecipeContext = createContext<RecipeContextValue | null>(null);
const STORAGE_KEY = "mijote-demo-recipes-v1";
const RECIPE_SELECT =
  "*, profiles!recipes_author_id_fkey(display_name, avatar_url), ingredients(*), recipe_steps(*), recipe_category_links(recipe_categories(name)), favorites(user_id), recipe_group_shares(group_id,sharing_groups!recipe_group_shares_group_id_fkey(id,name)), recipe_user_shares(user_id,profiles!recipe_user_shares_user_id_fkey(id,display_name,avatar_url))";

async function parseRecipe(row: Record<string, unknown>, userId: string): Promise<Recipe> {
  const authorRow = row.profiles as Record<string, unknown>;
  const ingredients = (row.ingredients as Record<string, unknown>[]).map((item) => ({
    id: String(item.id),
    name: String(item.name),
    quantity: item.quantity ? String(item.quantity) : undefined,
    unit: item.unit ? String(item.unit) : undefined,
    note: item.note ? String(item.note) : undefined,
    position: Number(item.position),
  }));
  const steps = (row.recipe_steps as Record<string, unknown>[]).map((item) => ({
    id: String(item.id),
    instruction: String(item.instruction),
    durationMinutes: item.duration_minutes === null ? undefined : Number(item.duration_minutes),
    illustrationId: item.illustration_id ? String(item.illustration_id) : undefined,
    position: Number(item.position),
  }));
  const links = row.recipe_category_links as Array<{ recipe_categories: { name: string } }>;
  const favorites = row.favorites as Array<{ user_id: string }>;
  const groupShares = (row.recipe_group_shares ?? []) as Array<{
    group_id: string;
    sharing_groups: { id: string; name: string };
  }>;
  const userShares = (row.recipe_user_shares ?? []) as Array<{
    user_id: string;
    profiles: { id: string; display_name: string; avatar_url: string | null };
  }>;
  let photoUrl: string | undefined;
  if (row.photo_path) {
    const { data } = await supabase!.storage.from("recipe-photos").createSignedUrl(String(row.photo_path), 60 * 60);
    photoUrl = data?.signedUrl;
  }
  return {
    id: String(row.id),
    author: {
      id: String(row.author_id),
      email: String(authorRow.email ?? ""),
      displayName: String(authorRow.display_name ?? "Membre"),
      avatarUrl: authorRow.avatar_url ? String(authorRow.avatar_url) : undefined,
    },
    title: String(row.title),
    description: String(row.description ?? ""),
    categories: links.map((link) => link.recipe_categories.name),
    servings: Number(row.servings),
    prepMinutes: Number(row.prep_minutes),
    cookMinutes: Number(row.cook_minutes),
    difficulty: row.difficulty as Recipe["difficulty"],
    photoUrl,
    photoPath: row.photo_path ? String(row.photo_path) : undefined,
    visibility: row.visibility as Recipe["visibility"],
    sharedGroups: groupShares.map((share) => ({
      id: share.sharing_groups.id,
      name: share.sharing_groups.name,
    })),
    sharedWith: userShares.map((share) => ({
      id: share.profiles.id,
      displayName: share.profiles.display_name,
      email: "",
      avatarUrl: share.profiles.avatar_url ?? undefined,
    })),
    ingredients: ingredients.sort((a, b) => a.position - b.position),
    steps: steps.sort((a, b) => a.position - b.position),
    createdAt: String(row.created_at),
    updatedAt: String(row.updated_at),
    isFavorite: favorites.some((favorite) => favorite.user_id === userId),
  };
}

export function RecipeProvider({ children }: { children: ReactNode }) {
  const { user, isDemo } = useAuth();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setRecipes([]);
      setLoading(false);
      return;
    }
    if (isDemo) {
      const saved = localStorage.getItem(STORAGE_KEY);
      const storedRecipes = saved ? (JSON.parse(saved) as Recipe[]) : demoRecipes;
      setRecipes(storedRecipes.map((recipe) => ({
        ...recipe,
        sharedGroups: recipe.sharedGroups ?? [],
        sharedWith: recipe.sharedWith ?? [],
      })));
      setLoading(false);
      return;
    }
    if (!supabase) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void supabase
      .from("recipes")
      .select(RECIPE_SELECT)
      .order("updated_at", { ascending: false })
      .then(async ({ data, error: loadError }) => {
        if (loadError) setError(loadError.message);
        else setRecipes(await Promise.all((data ?? []).map((row) => parseRecipe(row, user.id))));
        setLoading(false);
      });
  }, [isDemo, user]);

  const persist = useCallback((next: Recipe[]) => {
    setRecipes(next);
    if (isDemo) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, [isDemo]);

  const value = useMemo<RecipeContextValue>(
    () => ({
      recipes,
      loading,
      error,
      async saveRecipe(draft, recipeId, photoFile) {
        if (!user) throw new Error("Vous devez être connecté.");
        if (isDemo) {
          const previous = recipes.find((recipe) => recipe.id === recipeId);
          const now = new Date().toISOString();
          const { sharedGroupIds, sharedUserIds, ...recipeFields } = draft;
          const localPhotoUrl = photoFile
            ? await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result));
                reader.onerror = () => reject(new Error("Impossible de lire la photo."));
                reader.readAsDataURL(photoFile);
              })
            : draft.photoUrl;
          const saved: Recipe = {
            ...recipeFields,
            id: recipeId ?? createClientId(),
            author: user,
            photoUrl: localPhotoUrl,
            sharedGroups: sharedGroupIds.map((id) => ({
              id,
              name: id === "demo-close-family" ? "Parents et sœurs" : "Cercle familial",
            })),
            sharedWith: demoProfiles.filter((profile) => sharedUserIds.includes(profile.id)),
            createdAt: previous?.createdAt ?? now,
            updatedAt: now,
            isFavorite: previous?.isFavorite ?? false,
          };
          persist([saved, ...recipes.filter((recipe) => recipe.id !== recipeId)]);
          return saved;
        }
        if (!supabase) throw new Error("Supabase n’est pas configuré.");

        const previous = recipes.find((recipe) => recipe.id === recipeId);
        let photoPath: string | null | undefined;
        let uploadedPhotoPath: string | undefined;
        if (photoFile) {
          const extension = photoFile.type === "image/webp" ? "webp" : "jpg";
          photoPath = `${user.id}/${recipeId ?? createClientId()}/${createClientId()}.${extension}`;
          const { error: uploadError } = await supabase.storage.from("recipe-photos").upload(photoPath, photoFile, {
            contentType: photoFile.type,
            upsert: false,
          });
          if (uploadError) throw uploadError;
          uploadedPhotoPath = photoPath;
        } else if (draft.photoUrl === undefined) {
          photoPath = null;
        }

        const { data, error: saveError } = await supabase.rpc("save_recipe", {
          recipe_payload: {
            id: recipeId ?? null,
            title: draft.title,
            description: draft.description,
            categories: draft.categories,
            servings: draft.servings,
            prep_minutes: draft.prepMinutes,
            cook_minutes: draft.cookMinutes,
            difficulty: draft.difficulty ?? null,
            visibility: draft.visibility,
            shared_group_ids: draft.sharedGroupIds,
            shared_user_ids: draft.sharedUserIds,
            photo_path: photoPath,
            ingredients: draft.ingredients,
            steps: draft.steps.map((item) => ({
              ...item,
              duration_minutes: item.durationMinutes ?? null,
              illustration_id: item.illustrationId ?? null,
            })),
          },
        });
        if (saveError) {
          if (uploadedPhotoPath) {
            await supabase.storage.from("recipe-photos").remove([uploadedPhotoPath]);
          }
          throw saveError;
        }

        const savedPhotoPath = photoPath === undefined ? previous?.photoPath : photoPath ?? undefined;
        if (previous?.photoPath && previous.photoPath !== savedPhotoPath) {
          await supabase.storage.from("recipe-photos").remove([previous.photoPath]);
        }
        const { data: savedRow, error: reloadError } = await supabase
          .from("recipes")
          .select(RECIPE_SELECT)
          .eq("id", String(data))
          .single();
        if (reloadError) throw reloadError;
        const saved = await parseRecipe(savedRow, user.id);
        persist([saved, ...recipes.filter((recipe) => recipe.id !== recipeId)]);
        return saved;
      },
      async updateRecipeShares(recipeId, groupIds, userIds) {
        if (!user) throw new Error("Vous devez être connecté.");
        const recipe = recipes.find((item) => item.id === recipeId);
        if (!recipe || recipe.author.id !== user.id) {
          throw new Error("Seul l’auteur peut modifier les destinataires de cette recette.");
        }
        if (isDemo) {
          const updated: Recipe = {
            ...recipe,
            sharedGroups: groupIds.map((id) => ({
              id,
              name: id === "demo-close-family" ? "Parents et sœurs" : "Cercle familial",
            })),
            sharedWith: demoProfiles.filter((profile) => userIds.includes(profile.id)),
            updatedAt: new Date().toISOString(),
          };
          persist(recipes.map((item) => item.id === recipeId ? updated : item));
          return updated;
        }
        if (!supabase) throw new Error("Supabase n’est pas configuré.");
        const { error: shareError } = await supabase.rpc("update_recipe_shares", {
          target_recipe_id: recipeId,
          group_ids: groupIds,
          user_ids: userIds,
        });
        if (shareError) throw shareError;
        const { data: savedRow, error: reloadError } = await supabase
          .from("recipes")
          .select(RECIPE_SELECT)
          .eq("id", recipeId)
          .single();
        if (reloadError) throw reloadError;
        const updated = await parseRecipe(savedRow, user.id);
        persist(recipes.map((item) => item.id === recipeId ? updated : item));
        return updated;
      },
      async deleteRecipe(recipeId) {
        if (!user) return;
        if (!isDemo && supabase) {
          const { error: deleteError } = await supabase.from("recipes").delete().eq("id", recipeId);
          if (deleteError) throw deleteError;
        }
        persist(recipes.filter((recipe) => recipe.id !== recipeId));
      },
      async toggleFavorite(recipeId) {
        if (!user) return;
        const recipe = recipes.find((item) => item.id === recipeId);
        if (!recipe) return;
        if (!isDemo && supabase) {
          const query = recipe.isFavorite
            ? supabase.from("favorites").delete().eq("recipe_id", recipeId).eq("user_id", user.id)
            : supabase.from("favorites").insert({ recipe_id: recipeId, user_id: user.id });
          const { error: favoriteError } = await query;
          if (favoriteError) throw favoriteError;
        }
        persist(
          recipes.map((item) => (item.id === recipeId ? { ...item, isFavorite: !item.isFavorite } : item)),
        );
      },
    }),
    [error, isDemo, loading, persist, recipes, user],
  );

  return <RecipeContext.Provider value={value}>{children}</RecipeContext.Provider>;
}

export function useRecipes() {
  const value = useContext(RecipeContext);
  if (!value) throw new Error("useRecipes doit être utilisé dans RecipeProvider.");
  return value;
}
